import React, { Fragment, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { BiPlus } from "react-icons/bi";
import { roleAccess } from "helpers/RolesPermission";
import { USER_ROLE } from "Constants/constant";

const ParticipantProfileTabItem = (props) => {
  const { toggleTab, selectedTab, tabItem } = props;
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const isChildActive = tabItem.children?.some((x) => x.id === selectedTab);
  const isActive = selectedTab === tabItem.id || isChildActive;

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        !event.target.closest('.dropdown-menu-portal')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <>
      {tabItem.children && tabItem.children.length > 0 ? (
        <div className="relative flex-shrink-0">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`
              relative inline-flex items-center justify-center
              participant-tab-item rounded-lg font-medium
              transition-all duration-150
              whitespace-nowrap
              ${
                isActive
                  ? "bg-white/20 text-white shadow-sm"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }
            `}
          >
            <span>{tabItem.name}</span>
            <ChevronDownIcon
              className={`participant-tab-icon transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
              aria-hidden='true'
            />
          </button>
          {typeof window !== 'undefined' && isOpen && createPortal(
            <div 
              className='dropdown-menu-portal fixed w-72 max-w-[min(20rem,calc(100vw-2rem))] origin-top-left rounded-lg bg-white shadow-xl ring-1 ring-black/5 z-[10000] py-2 overflow-hidden flex flex-col'
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                maxHeight: "min(70vh, 22rem)",
              }}
            >
              <div className='py-0.5 overflow-y-auto overflow-x-hidden flex-1 min-h-0 custom-scroll'>
                {tabItem.children.map((childTab) => (
                  <div key={childTab.id ?? childTab.slug ?? childTab.name}>
                    <div className='flex items-center justify-between px-1'>
                      <button
                        type="button"
                        onClick={() => {
                          toggleTab(childTab);
                          setIsOpen(false);
                        }}
                        className={`
                          flex-1 block px-4 py-2.5 text-sm rounded-md text-left transition-all duration-150
                          ${
                            selectedTab === childTab.id
                              ? "bg-blue-50 text-blue-700 font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }
                        `}
                      >
                        {childTab.name}
                      </button>
                      {roleAccess([
                        USER_ROLE.Sp_Admin,
                        USER_ROLE.Sp_manager,
                      ]) && childTab.slug && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTab({ ...childTab, isModalOpen: true });
                            setIsOpen(false);
                          }}
                          className={`
                            ml-2 p-1.5 rounded-md transition-all duration-150
                            ${
                              selectedTab === childTab.id
                                ? "text-blue-600 hover:bg-blue-100"
                                : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            }
                          `}
                          title="Add New"
                        >
                          <BiPlus className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>,
            document.body
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => toggleTab(tabItem)}
          className={`
            relative inline-flex items-center justify-center
            participant-tab-item rounded-lg font-medium
            transition-all duration-150
            whitespace-nowrap
            ${
              isActive
                ? "bg-white/20 text-white shadow-sm"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }
          `}
        >
          <span>{tabItem.name}</span>
        </button>
      )}
    </>
  );
};

export default ParticipantProfileTabItem;
